import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import FormData from 'form-data';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    resetController,
    setController,
    setFilters,
    setInitialFilters,
} from '../../../../store/slices/tableController';
import StatusCard from '../../../../components/common/StatusUI/StatusCard';
import { Plus as PlusIcon } from '../../../../assets/icons/plus';
import { Upload as UploadIcon } from '../../../../assets/icons/upload';
import { styles } from './styles';
import CustomTable from '../../../../components/common/CustomTable';
import PositionTableActions from './PositionTableActions';
import { columns } from './columns';
import {
    STATUS_PENDING_APPROVAL,
    STATUS_IN_DRAFT,
    STATUS_REQUISITION_APPROVED,
    STATUS_DRAFT_CHANGE_REQUESTED,
    TOTAL_POSITIONS,
    POSITION_CREATED,
    STATUS_REQUISITION_REVIEW,
} from '../../../../constants';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DehazeIcon from '@mui/icons-material/Dehaze';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import VerifiedIcon from '@mui/icons-material/Verified';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import {
    useGetPreHiringPositionsQuery,
    useListReviewPoliciesMutation,
    useBulkApprovePositionsFormDataMutation,
} from '../../../../api/preHQ';
import {
    setReviewPolicies,
    updateAllSelectedPositions,
    updatePositions,
    updateSelectedPositions,
} from '../../../../store/slices/preHQPositions';
import { hasPermission } from '../../../../utils/permission';
import PermissionChecker from '../../../../components/common/PermissionChecker';
import ConfirmationDialog from '../../../../components/common/Dialog/ConfirmationDialog';

const metrics = [
    {
        id: TOTAL_POSITIONS,
        title: TOTAL_POSITIONS,
        key: 'totalCount',
        icon: AutoAwesomeMotionIcon,
    },
    {
        id: STATUS_IN_DRAFT,
        title: STATUS_IN_DRAFT,
        key: STATUS_IN_DRAFT,
        icon: TextSnippetIcon,
    },
    {
        id: STATUS_REQUISITION_REVIEW,
        title: STATUS_REQUISITION_REVIEW,
        key: STATUS_REQUISITION_REVIEW,
        icon: RateReviewIcon,
    },
    {
        id: STATUS_PENDING_APPROVAL,
        title: STATUS_PENDING_APPROVAL,
        key: STATUS_PENDING_APPROVAL,
        icon: AssignmentIcon,
    },
    {
        id: STATUS_DRAFT_CHANGE_REQUESTED,
        title: STATUS_DRAFT_CHANGE_REQUESTED,
        key: STATUS_DRAFT_CHANGE_REQUESTED,
        icon: DehazeIcon,
    },
    {
        id: STATUS_REQUISITION_APPROVED,
        title: STATUS_REQUISITION_APPROVED,
        key: STATUS_REQUISITION_APPROVED,
        icon: DomainVerificationIcon,
    },
    {
        id: POSITION_CREATED,
        title: POSITION_CREATED,
        key: POSITION_CREATED,
        icon: VerifiedIcon,
    },
];

const PositionsList = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const controller = useSelector((state) => state.tableController);
    const currentUser = useSelector((state) => state.session.user);
    const { selectedPositions } = useSelector((state) => state.preHQPositions);

    const [selectedStatus, setSelectedStatus] = useState(TOTAL_POSITIONS);
    const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
    const [readyForInternalHQOnly, setReadyForInternalHQOnly] = useState(false);

    const { data: allPositionsData, refetch: refetchAllPositions } = useGetPreHiringPositionsQuery();
    const {
        data: positionData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useGetPreHiringPositionsQuery({
        status: selectedStatus === TOTAL_POSITIONS ? null : selectedStatus,
        offset: controller?.page * controller?.pageSize,
        max: controller?.pageSize,
        readyForInternalHq: readyForInternalHQOnly || undefined,
    });

    const [listReviewPolicies] = useListReviewPoliciesMutation();
    const [bulkApprovePositionsFormData] = useBulkApprovePositionsFormDataMutation();
    useEffect(() => {
        if (!isLoading && positionData) dispatch(updatePositions(positionData));
    }, [isLoading, positionData]);

    useEffect(() => {
        const formData = new FormData();
        formData.append('offset', 0);
        formData.append('max', 100);
        formData.append('status', 'Active');
        listReviewPolicies(formData)
            .unwrap()
            .then((res) => {
                dispatch(setReviewPolicies(res?.reviewPolicies));
            });
    }, []);

    useEffect(() => {
        refetchAllPositions();
        refetch();
    }, []);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (id) => {
        navigate(`/client/positions/details?positionId=${id}&action=view`);
    };

    const handleSelect = (event, rowId) => {
        dispatch(
            updateSelectedPositions({
                isChecked: event.target.checked,
                rowId,
            }),
        );
    };

    const handleSelectAll = (event) => {
        dispatch(
            updateAllSelectedPositions({
                isChecked: event.target.checked,
                positions: positionData?.preHiringPositions,
            }),
        );
    };

    const handleStatusCardSelect = (value) => {
        dispatch(onPageChange(0));
        if (!value) setSelectedStatus(null);
        else setSelectedStatus(value);
    };

    const handleReadyForInternalHQChange = (checked) => {
        setReadyForInternalHQOnly(checked);
        dispatch(onPageChange(0));
    };

    const handleBulkUpdateConfirm = async () => {
        try {
            const formData = new FormData();
            formData.append('ids', selectedPositions.join(','));

            await bulkApprovePositionsFormData(formData).unwrap();
            setShowBulkUpdateModal(false);
            dispatch(updateAllSelectedPositions({ isChecked: false, positions: [] }));
            refetch();
            enqueueSnackbar(`Successfully moved ${selectedPositions.length} position(s) to draft`, {
                variant: 'success',
            });
        } catch (error) {
            console.error('Bulk update failed:', error);
            const errorMessage =
                error?.data?.message || error?.message || 'Failed to update positions. Please try again.';
            enqueueSnackbar(errorMessage, {
                variant: 'error',
            });
        } finally {
            setShowBulkUpdateModal(false);
        }
    };

    const displayBulkEdit = selectedStatus === STATUS_IN_DRAFT || selectedStatus === STATUS_DRAFT_CHANGE_REQUESTED;

    // Check if all selected positions have STATUS_REQUISITION_APPROVED status and readyForInternalHq is true
    const displayBulkApprove = useMemo(() => {
        if (selectedPositions.length === 0) return false;

        const currentPositions = positionData?.preHiringPositions || [];

        return selectedPositions.every((selectedId) => {
            const position = currentPositions.find((pos) => pos.id === selectedId);
            return position?.status === STATUS_REQUISITION_APPROVED && position?.readyForInternalHq === true;
        });
    }, [selectedPositions, positionData?.preHiringPositions]);

    const hasViewPermission = useMemo(
        () => hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_VIEW_POSITIONS']),
        [currentUser.authorities],
    );

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Requisition</Typography>
                <Box>
                    <PermissionChecker
                        requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_BULK_UPLOAD_POSITIONS']}
                        noAuthRedirect={false}
                    >
                        <Button
                            color="primary"
                            onClick={() => navigate('/client/positions/bulk-upload')}
                            size="large"
                            startIcon={<UploadIcon fontSize="small" />}
                            variant="outlined"
                        >
                            Bulk Upload
                        </Button>
                    </PermissionChecker>
                    <PermissionChecker
                        requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_BULK_EDIT_POSITIONS']}
                        noAuthRedirect={false}
                    >
                        {displayBulkApprove && (
                            <Button
                                color="primary"
                                onClick={() => setShowBulkUpdateModal(true)}
                                size="large"
                                startIcon={<KeyboardDoubleArrowRightIcon fontSize="small" />}
                                variant="contained"
                                sx={{
                                    ml: '24px',
                                    ':hover': {
                                        backgroundColor: 'secondary.main',
                                    },
                                }}
                            >
                                Bulk Move to Draft
                            </Button>
                        )}
                    </PermissionChecker>
                    <PermissionChecker
                        requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_BULK_EDIT_POSITIONS']}
                        noAuthRedirect={false}
                    >
                        {displayBulkEdit && (
                            <Button
                                color="primary"
                                onClick={() => navigate('/client/positions/bulk-edit')}
                                size="large"
                                startIcon={<PlusIcon fontSize="small" />}
                                variant="contained"
                                sx={{
                                    ml: '24px',
                                    ':hover': {
                                        backgroundColor: 'secondary.main',
                                    },
                                }}
                            >
                                Bulk Edit
                            </Button>
                        )}
                    </PermissionChecker>
                    <PermissionChecker
                        requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_CREATE_POSITIONS']}
                        noAuthRedirect={false}
                    >
                        <Button
                            color="primary"
                            onClick={() => navigate('/client/positions/create-position')}
                            size="large"
                            startIcon={<PlusIcon fontSize="small" />}
                            variant="contained"
                            sx={{
                                ml: '24px',
                                ':hover': {
                                    backgroundColor: 'secondary.main',
                                },
                            }}
                        >
                            Add Requisition
                        </Button>
                    </PermissionChecker>
                </Box>
            </Box>
            <Box pb="32px">
                <Grid item container spacing={3} justifyContent="flex-start" alignItems="stretch" columns={15}>
                    {metrics.map((item) => {
                        return (
                            <Grid item xs={12} sm={3} md={3} key={item.id}>
                                <StatusCard
                                    Icon={item.icon}
                                    label={item.title}
                                    filteredStatus={selectedStatus}
                                    value={
                                        (item.title === TOTAL_POSITIONS
                                            ? allPositionsData?.['totalCount']
                                            : allPositionsData?.statusWiseCount?.[item.key]) || '0'
                                    }
                                    handleStatusFilter={handleStatusCardSelect}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <PositionTableActions
                            filters={controller.filters}
                            initialFilters={controller.initialFilters}
                            onFiltersApply={handleFiltersApply}
                            onFiltersClear={handleFiltersClear}
                            selectedPositions={selectedPositions}
                            filterProperties={columns}
                            action={refetch}
                            readyForInternalHQOnly={readyForInternalHQOnly}
                            onReadyForInternalHQChange={handleReadyForInternalHQChange}
                            selectedStatus={selectedStatus}
                        />
                        <CustomTable
                            isLoading={isLoading || isFetching}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSelect={handleSelect}
                            onSelectAll={handleSelectAll}
                            onRowClick={hasViewPermission && handleRowClick}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={positionData?.preHiringPositions}
                            rowsCount={positionData?.totalCount}
                            selectedRows={selectedPositions}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={true}
                            showPagignation={true}
                            keyColumn="id"
                            action={refetch}
                        />
                    </CardContent>
                </Card>
            </Box>
            <ConfirmationDialog
                open={showBulkUpdateModal}
                title="Bulk Update Positions"
                message={`Are you sure you want to move ${selectedPositions.length} ${selectedPositions.length > 1 ? 'positions' : 'position'} to draft?`}
                onCancel={() => setShowBulkUpdateModal(false)}
                onConfirm={handleBulkUpdateConfirm}
            />
        </Box>
    );
};

export default PositionsList;
